import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { z } from "zod";
import { fallback } from "@tanstack/zod-adapter";
import { toast } from "sonner";
import confetti from "canvas-confetti";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/analytics";
import { trackMetaEvent } from "@/lib/metaPixel";

const successSearchSchema = z.object({
  session_id: fallback(z.string().optional(), undefined).optional(),
});

export const Route = createFileRoute("/_authenticated/billing/success")({
  validateSearch: successSearchSchema,
  head: () => ({
    meta: [
      { title: "決済完了 - KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: BillingSuccessPage,
});

type SessionInfo = {
  mode: "subscription" | "payment";
  plan_label: string | null;
  ticket_count: number | null;
  amount_total: number;
  currency: string;
  payment_status: string;
};

const REFLECTION_TIMEOUT_MS = 10_000;

function fireConfetti() {
  // Two bursts from the bottom-left and bottom-right.
  const defaults = { spread: 70, ticks: 200, gravity: 0.9, scalar: 1 };
  confetti({ ...defaults, particleCount: 80, angle: 60, origin: { x: 0, y: 0.8 } });
  confetti({ ...defaults, particleCount: 80, angle: 120, origin: { x: 1, y: 0.8 } });
  setTimeout(() => {
    confetti({ ...defaults, particleCount: 60, angle: 90, origin: { x: 0.5, y: 0.6 } });
  }, 250);
}

function BillingSuccessPage() {
  const { session_id } = useSearch({ from: "/_authenticated/billing/success" });

  const [info, setInfo] = useState<SessionInfo | null>(null);
  const [infoError, setInfoError] = useState<string | null>(null);

  const [balance, setBalance] = useState<number | null>(null);
  const [reflected, setReflected] = useState(false);
  const [reflectionTimedOut, setReflectionTimedOut] = useState(false);

  const initialBalanceRef = useRef<number | null>(null);
  const confettiFiredRef = useRef(false);
  const toastFiredRef = useRef(false);

  // 1) Fetch session info from edge function.
  useEffect(() => {
    if (!session_id) {
      setInfoError("セッションIDが見つかりません");
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.functions.invoke("get-checkout-session", {
        body: { session_id },
      });
      if (cancelled) return;
      if (error || !data) {
        setInfoError("購入情報の取得に失敗しました");
        return;
      }
      const sessionInfo = data as SessionInfo;
      setInfo(sessionInfo);
      // Analytics: purchase or subscribe
      if (sessionInfo.mode === "subscription") {
        track("subscribe", {
          plan: sessionInfo.plan_label ?? null,
          period: "monthly",
        });
        trackMetaEvent("Subscribe", {
          value: sessionInfo.amount_total,
          currency: (sessionInfo.currency || "JPY").toUpperCase(),
          predicted_ltv: sessionInfo.amount_total,
          subscription_id: sessionInfo.plan_label ?? undefined,
        });
        // Subscribe を Purchase としても送って広告コンバージョン計測に利用
        trackMetaEvent("Purchase", {
          value: sessionInfo.amount_total,
          currency: (sessionInfo.currency || "JPY").toUpperCase(),
          content_type: "subscription",
          content_name: sessionInfo.plan_label ?? undefined,
        });
      } else {
        track("purchase", {
          item: "tickets",
          amount_jpy: sessionInfo.amount_total,
          tickets: sessionInfo.ticket_count ?? null,
        });
        trackMetaEvent("Purchase", {
          value: sessionInfo.amount_total,
          currency: (sessionInfo.currency || "JPY").toUpperCase(),
          content_type: "credits",
          contents: sessionInfo.ticket_count
            ? [{ id: "tickets", quantity: sessionInfo.ticket_count }]
            : undefined,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session_id]);

  // 2) Toast on mount.
  useEffect(() => {
    if (toastFiredRef.current) return;
    toastFiredRef.current = true;
    toast.success("決済が完了しました");
  }, []);

  // 3) Watch credits table for change. Subscribe + initial fetch + poll fallback + timeout.
  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let timeoutTimer: ReturnType<typeof setTimeout> | null = null;

    const handleCredits = (purchased: number, subscription: number) => {
      const total = (purchased ?? 0) + (subscription ?? 0);
      if (initialBalanceRef.current === null) {
        initialBalanceRef.current = total;
        setBalance(total);
        return;
      }
      setBalance(total);
      if (total > initialBalanceRef.current && !reflected) {
        setReflected(true);
        if (!confettiFiredRef.current) {
          confettiFiredRef.current = true;
          fireConfetti();
        }
      }
    };

    const fetchOnce = async () => {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData.user?.id;
      if (!uid) return;
      const { data } = await supabase
        .from("credits")
        .select("credit_purchased_balance, credit_subscription_balance")
        .eq("user_id", uid)
        .maybeSingle();
      if (cancelled || !data) return;
      handleCredits(data.credit_purchased_balance ?? 0, data.credit_subscription_balance ?? 0);
    };

    void fetchOnce();
    pollTimer = setInterval(() => void fetchOnce(), 1500);

    const channel = supabase
      .channel("billing-success-credits")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "credits" },
        (payload) => {
          const row = (payload.new ?? payload.old) as
            | { credit_purchased_balance?: number; credit_subscription_balance?: number }
            | null;
          if (!row) return;
          handleCredits(row.credit_purchased_balance ?? 0, row.credit_subscription_balance ?? 0);
        },
      )
      .subscribe();

    timeoutTimer = setTimeout(() => {
      if (!reflected) setReflectionTimedOut(true);
      if (pollTimer) clearInterval(pollTimer);
    }, REFLECTION_TIMEOUT_MS);

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
      if (timeoutTimer) clearTimeout(timeoutTimer);
      void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stop polling once reflected.
  useEffect(() => {
    if (reflected && !confettiFiredRef.current) {
      confettiFiredRef.current = true;
      fireConfetti();
    }
  }, [reflected]);

  const purchaseSummary = (() => {
    if (!info) return null;
    if (info.mode === "subscription") {
      return info.plan_label
        ? `${info.plan_label}プランへようこそ！`
        : "サブスクリプションを開始しました";
    }
    if (info.ticket_count) {
      return `${info.ticket_count}クレジットを購入しました`;
    }
    return "ご購入を確認しました";
  })();

  return (
    <main className="min-h-screen bg-background px-4 py-12">
      <div className="mx-auto max-w-md">
        <Card className="border-primary/30 shadow-lg">
          <CardContent className="flex flex-col items-center gap-5 p-8 text-center">
            {/* Animated check icon */}
            <div
              className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/15 animate-scale-in"
              style={{ animationDuration: "0.5s" }}
            >
              <CheckCircle2
                className="h-12 w-12 text-emerald-500 animate-fade-in"
                style={{ animationDuration: "0.6s" }}
              />
            </div>

            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              ご購入ありがとうございます！
            </h1>

            {/* Purchase summary */}
            {info ? (
              <p className="text-base font-medium text-foreground">{purchaseSummary}</p>
            ) : infoError ? (
              <p className="text-sm text-muted-foreground">{infoError}</p>
            ) : (
              <Skeleton className="h-5 w-2/3" />
            )}

            {/* Balance */}
            <div className="w-full rounded-lg border border-border bg-muted/40 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                現在のクレジット残高
              </p>
              {reflected || reflectionTimedOut ? (
                <p className="mt-1 text-3xl font-bold text-foreground">
                  {balance ?? 0}
                  <span className="ml-1 text-sm font-normal text-muted-foreground">枚</span>
                </p>
              ) : (
                <div className="mt-2 flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">反映処理中…</span>
                </div>
              )}
              {reflectionTimedOut && !reflected && (
                <p className="mt-2 text-xs text-muted-foreground">
                  処理に少しお時間がかかっています。ダッシュボードでご確認ください。
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="mt-2 flex w-full flex-col gap-2">
              <Button
                asChild
                className="h-11 w-full bg-gradient-brand text-white shadow-lg shadow-brand-primary/30 hover:opacity-95"
              >
                <Link to="/create">
                  <Sparkles className="h-4 w-4" />
                  <span>動画を作る</span>
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-11 w-full">
                <Link to="/dashboard">ダッシュボードへ</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
